import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff, Lock, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        {},
        {
          auth: {
            username,
            password,
          },
        }
      );

      if (response.data.success) {
        sessionStorage.setItem("adminAuth", btoa(`${username}:${password}`));
        toast.success("Giriş başarılı!");
        navigate("/admin/urunler");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Kullanıcı adı veya şifre hatalı!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 p-4" data-testid="admin-login-page">
      <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-10 w-full max-w-sm lg:max-w-md">
        {/* Logo */}
        <div className="text-center mb-6 lg:mb-8">
          <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4 shadow-lg">
            <span className="text-white text-2xl lg:text-4xl font-bold font-serif">C</span>
          </div>
          <h1 
            className="text-2xl lg:text-3xl font-bold text-slate-800 mb-1 lg:mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            CK-DER Admin
          </h1>
          <p className="text-slate-500 text-sm lg:text-base">Yönetim Paneli Girişi</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4 lg:space-y-6">
          <div className="space-y-1.5 lg:space-y-2">
            <Label htmlFor="username" className="text-slate-700 font-medium text-sm lg:text-base">
              Kullanıcı Adı
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 lg:w-5 lg:h-5" />
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Kullanıcı adınızı girin"
                className="pl-9 lg:pl-10 h-10 lg:h-12 text-sm lg:text-base border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                required
                data-testid="username-input"
              />
            </div>
          </div>

          <div className="space-y-1.5 lg:space-y-2">
            <Label htmlFor="password" className="text-slate-700 font-medium text-sm lg:text-base">
              Şifre
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 lg:w-5 lg:h-5" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifrenizi girin"
                className="pl-9 lg:pl-10 pr-9 lg:pr-10 h-10 lg:h-12 text-sm lg:text-base border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff className="w-4 h-4 lg:w-5 lg:h-5" /> : <Eye className="w-4 h-4 lg:w-5 lg:h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-10 lg:h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold text-sm lg:text-base"
            data-testid="login-button"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 lg:w-5 lg:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Giriş yapılıyor...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <LogIn className="w-4 h-4 lg:w-5 lg:h-5" />
                <span>Giriş Yap</span>
              </div>
            )}
          </Button>
        </form>

        {/* Back to Home */}
        <div className="mt-4 lg:mt-6 text-center">
          <a 
            href="/" 
            className="text-amber-600 hover:text-amber-700 text-xs lg:text-sm font-medium"
            data-testid="back-to-home"
          >
            ← Ana Sayfaya Dön
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
